/**
 * elkToReactFlow.js — Convert a laid-out ELK hierarchical graph to React
 * Flow `{nodes, edges}` props.
 *
 * ELK hands back a tree of `children[]` with per-level coordinates relative
 * to the parent. React Flow expects:
 *   - a flat `nodes` array where positions are in a single global coordinate
 *     space (unless you use parentId + extent='parent', which is what we do
 *     here so the container shows its own bbox with children inside).
 *   - a flat `edges` array referencing source/target node ids.
 *
 * The custom node types / edge types are keyed by the `_v2.kind` tag the
 * builder stamped in `flatToElkHierarchical.js`. The mapping below is the
 * single source of truth tying builder kind → React Flow node `type`.
 */

const KIND_TO_RF_TYPE = {
  start:                'v2Sentinel',
  end:                  'v2Sentinel',
  leaf:                 'v2Leaf',
  switchHead:           'v2SwitchHead',
  switchContainer:      'v2SwitchContainer',
  caseContainer:        'v2CaseContainer',
  meet:                 'v2Meet',
  terminate:            'v2Terminate',
  // Fork family (task #161)
  forkContainer:        'v2ForkContainer',
  forkHead:             'v2ForkHead',
  legContainer:         'v2LegContainer',
  join:                 'v2Join',
  dynForkContainer:     'v2DynForkContainer',
  dynForkHead:          'v2DynForkHead',
  dynLegTemplate:       'v2DynLegTemplate',
  // SubWorkflow (task #162) — scalar head + container for inline-embedded
  subWorkflow:          'v2SubWorkflow',
  subWorkflowContainer: 'v2SubWorkflowContainer',
  subWorkflowHead:      'v2SubWorkflowHead',
  // DoWhile / Loop (task #162) — container with head + body + back-edge
  loop:                 'v2LoopHead', // scalar fallback (should not happen post-#162)
  loopContainer:        'v2LoopContainer',
  loopHead:             'v2LoopHead',
  loopBody:             'v2LoopBody',
};

/**
 * Convert a laid-out ELK graph into React Flow node/edge arrays.
 *
 * @param {object} laid ELK graph as returned by `elk.layout()` — same shape
 *   as the input, but every node/edge has absolute x/y stamped on it.
 * @returns {{ rfNodes: object[], rfEdges: object[] }}
 */
export function elkToReactFlow(laid) {
  const rfNodes = [];
  const rfEdges = [];
  walk(laid, null, 0, 0, rfNodes, rfEdges);

  // G267 — Cross-cutting edges (cognitive availability/return) attached as
  // `_crossCutting` on the root by flatToElkHierarchical. ELK doesn't lay
  // these out (their endpoints may be orphans pinned by V2CanvasView post
  // ELK), so we emit them as React Flow edges with no bend points — V2Edge
  // falls back to a straight line between the handles.
  if (Array.isArray(laid?._crossCutting)) {
    /* G267-fix — Endpoint id resolution.
     *
     * Cross-cutting edges are emitted with CANVAS node ids (`from`/`to` off the flat
     * payload). But a laid-out node's React Flow id is the QUALIFIED ELK id
     * (`_qualify(kind, irId)`), with the canvas id stashed in `data.sourceNodeId`. So a
     * cross-cutting `source` pointing at the laid-out cognitive_loop never matched any
     * rfNode id, and React Flow silently dropped the edge — which is why the "may invoke" /
     * "return" edges never drew, even though the data carried them.
     *
     * Map canvas id → rf id for every laid-out node. Orphans pinned later by V2CanvasView
     * are keyed BY canvas id, so an unmapped endpoint correctly falls through unchanged. */
    const canvasToRf = new Map();
    for (const n of rfNodes) {
      const sid = n && n.data && n.data.sourceNodeId;
      if (sid && !canvasToRf.has(sid)) canvasToRf.set(sid, n.id);
    }
    for (const e of laid._crossCutting) {
      rfEdges.push({
        id: e.id,
        source: canvasToRf.get(e.source) ?? e.source,
        /* The catalog group is synthesised by V2CanvasView AFTER this pass, so its id is
         * not in canvasToRf and correctly falls through unchanged. */
        target: canvasToRf.get(e.target) ?? e.target,
        /* G267c — Anchor on the loop's RIGHT edge and the group's LEFT edge. Omitting these
         * let ReactFlow fall back to the loop's only source handle (bottom), which dropped
         * the route under the loop and doubled it back across the trunk. */
        sourceHandle: e.sourceHandle || null,
        targetHandle: e.targetHandle || null,
        type: 'v2Edge',
        data: {
          variant: e.variant,
          branchLabel: null,
          isDefault: false,
          bendPoints: [],
          labelPos: null,
          labelText: e.label || null,
          /* Bidirectional: the loop invokes into the catalog, results return from it. */
          arrowForward: true,
          arrowBack: true,
          toolCount: e.toolCount ?? 0,
        },
        style: variantEdgeStyle(e.variant),
      });
    }
  }

  return { rfNodes, rfEdges };
}

function walk(elkNode, parentId, parentAbsX, parentAbsY, rfNodes, rfEdges) {
  const isRoot = !parentId;
  const absX = (elkNode.x ?? 0) + parentAbsX;
  const absY = (elkNode.y ?? 0) + parentAbsY;

  if (!isRoot) {
    const meta = elkNode._v2 || {};
    const rfType = KIND_TO_RF_TYPE[meta.kind] || 'default';
    // Containers in React Flow use `parentId` + `extent='parent'` so their
    // children visually sit inside them. We ALSO pass width/height on the
    // container itself so it paints a proper chrome rect. Leaves use
    // absolute coords (no parentId) to keep edge routing simple — we
    // recompute their absolute position from the ELK hierarchy.
    const isContainer = Array.isArray(elkNode.children) && elkNode.children.length > 0;

    const rfNode = {
      id: elkNode.id,
      type: rfType,
      // Containers still get absolute positions — parentId nesting in React
      // Flow complicates z-index and edge routing more than it's worth at
      // this scale. We paint the container chrome at (absX, absY) with
      // (width, height), and child leaves likewise get absolute coordinates.
      position: { x: absX, y: absY },
      width: elkNode.width ?? undefined,
      height: elkNode.height ?? undefined,
      // Explicit style so React Flow reserves the exact footprint ELK
      // computed — otherwise container chrome collapses to content-hugging.
      style: {
        width: elkNode.width ?? undefined,
        height: elkNode.height ?? undefined,
      },
      data: {
        ...meta,
        isContainer,
      },
      // Containers draw behind their children. We bump z-index of leaves
      // via the render order (leaves are always pushed after the container
      // they live in during the walk).
      //
      // Drag policy:
      //   - LEAF + TERMINATE nodes are draggable. Users frequently want
      //     to nudge a task to a different vertical slot; ReactFlow's
      //     onNodeDrag updates `position` on the node and the next ELK
      //     re-layout (on save / reload) picks the canonical position
      //     back up — so drags are a session-scoped visual aid, not a
      //     persistent override (yet).
      //   - SENTINELS (start/end) stay locked. They bookend the trunk;
      //     letting them roam doesn't have a useful semantic.
      //   - CONTAINER CHROME (switchContainer/forkContainer/legContainer/
      //     caseContainer/switchHead/forkHead/join/meet) stays locked —
      //     ELK owns their geometry and dragging them collapses the
      //     visual hierarchy.
      draggable: meta.kind === 'leaf' || meta.kind === 'terminate',
      selectable: meta.kind !== 'meet',
    };
    rfNodes.push(rfNode);

    // Edges owned by this subtree resolve to absolute bend points by adding
    // the subtree's (absX, absY) to every edge coordinate — that's how ELK
    // encodes nested edges.
    if (Array.isArray(elkNode.edges)) {
      for (const e of elkNode.edges) {
        rfEdges.push(convertEdge(e, absX, absY));
      }
    }
  } else if (Array.isArray(elkNode.edges)) {
    // Root-level edges use the root's (0,0) origin.
    for (const e of elkNode.edges) rfEdges.push(convertEdge(e, 0, 0));
  }

  if (Array.isArray(elkNode.children)) {
    for (const child of elkNode.children) {
      walk(child, elkNode.id || 'root', absX, absY, rfNodes, rfEdges);
    }
  }
}

function convertEdge(e, baseX, baseY) {
  const meta = e._v2 || {};
  const variant = meta.variant || 'trunk';
  const section = (e.sections && e.sections[0]) || null;
  const bendPoints = [];
  if (section) {
    if (section.startPoint) bendPoints.push({ x: section.startPoint.x + baseX, y: section.startPoint.y + baseY });
    if (section.bendPoints) {
      for (const bp of section.bendPoints) bendPoints.push({ x: bp.x + baseX, y: bp.y + baseY });
    }
    if (section.endPoint) bendPoints.push({ x: section.endPoint.x + baseX, y: section.endPoint.y + baseY });
  }

  // Label position is in the edge owner's local coords too.
  const rawLabel = (e.labels && e.labels[0]) || null;
  const labelPos = rawLabel
    ? {
        x: (rawLabel.x ?? 0) + (rawLabel.width || 0) / 2 + baseX,
        y: (rawLabel.y ?? 0) + (rawLabel.height || 0) / 2 + baseY,
      }
    : null;

  return {
    id: e.id,
    source: e.sources?.[0],
    target: e.targets?.[0],
    type: 'v2Edge',
    data: {
      variant,
      branchLabel: meta.branchLabel || null,
      isDefault: !!meta.isDefault,
      bendPoints,
      labelPos,
      labelText: rawLabel ? rawLabel.text : null,
    },
    style: variantEdgeStyle(variant),
  };
}

function variantEdgeStyle(variant) {
  // Mirrors V2_PAL.edge in v2Palette.js. Kept in-file because this module
  // has no React dependencies and we don't want to import a palette just for
  // two colour codes; any palette change should be made in both places.
  switch (variant) {
    case 'convergence':
      return { stroke: '#94a3b8', strokeWidth: 1.5, strokeDasharray: '4 4' };
    case 'branch':
      return { stroke: '#475569', strokeWidth: 1.75 };
    case 'loopBack':
      // DoWhile iteration back-edge — orange-600, dashed, distinct from
      // trunk/convergence so the eye can follow the loop intuition.
      return { stroke: '#ea580c', strokeWidth: 1.75, strokeDasharray: '5 3' };
    case 'availability':
      // G267 — Cognitive-loop "may invoke" edge per AGENT_EXECUTION_MODEL §4.
      // Cyan-600, longer dashes than convergence so it reads as a
      // capability/tool-binding hint rather than a structural converge.
      return { stroke: '#0891b2', strokeWidth: 1.5, strokeDasharray: '6 4' };
    case 'cognitiveReturn':
      // Skill → loop "return" edge — same hue, shorter dots so the
      // direction of the iteration reads at a glance against the
      // dashed availability edges.
      return { stroke: '#0891b2', strokeWidth: 1.25, strokeDasharray: '2 3' };
    case 'trunk':
    default:
      return { stroke: '#475569', strokeWidth: 1.75 };
  }
}
